import { useEffect, useRef } from "react";

/**
 * Textarea that grows with its content — no internal scrolling until it
 * would exceed `maxRows`, at which point it caps and scrolls. Used for
 * instruction steps where a single-line rows={2} was hiding text on
 * longer descriptions.
 *
 * Sizing is done imperatively (measure scrollHeight, set style.height)
 * so it works with controlled inputs, autofilled values, and paste events
 * with no dependency on any external hook library.
 */
export default function AutoGrowTextarea({
  value,
  onChange,
  minRows = 2,
  maxRows = 20,
  className = "",
  ...rest
}) {
  const ref = useRef(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    // Compute the pixel height of one line so we can cap at maxRows.
    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const borderY = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

    el.style.height = "auto";
    const contentHeight = el.scrollHeight;
    const maxHeight = maxRows * lineHeight + paddingY + borderY;
    const minHeight = minRows * lineHeight + paddingY + borderY;
    const finalHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));
    el.style.height = `${finalHeight}px`;
    el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  };

  // Resize whenever the value changes (typing, paste, external updates).
  useEffect(() => { resize(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  // Also on mount so pre-filled values sit at the right height.
  useEffect(() => { resize(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <textarea
      ref={ref}
      value={value ?? ""}
      onChange={onChange}
      rows={minRows}
      className={className}
      {...rest}
    />
  );
}
